import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDesktop,
  faMobileScreen,
  faTabletScreenButton,
  faCircleQuestion,
  faChartLine,
  faClock,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { fromServer, ago, DISPLAY_TZ } from './Servertime';
import './UserTrackingTab.css';

const WHO = { teacher: 'Teacher', student: 'Student', demostudent: 'Demo student' };

const DEVICES = {
  desktop: { label: 'Desktop', icon: faDesktop },
  mobile: { label: 'Mobile', icon: faMobileScreen },
  tablet: { label: 'Tablet', icon: faTabletScreenButton },
  unknown: { label: 'Unknown', icon: faCircleQuestion },
};
const deviceKey = (t) => {
  const k = String(t || '').toLowerCase();
  return DEVICES[k] ? k : 'unknown';
};

/* Page name: title first, else the path of the URL (same as before, but never crashes on a bad URL) */
const pageOf = (visit) => {
  if (visit.PageTitle) return visit.PageTitle.length > 60 ? `${visit.PageTitle.slice(0, 60)}…` : visit.PageTitle;
  if (visit.PageUrl) {
    try {
      return new URL(visit.PageUrl, window.location.origin).pathname;
    } catch {
      return visit.PageUrl;
    }
  }
  return 'Unknown page';
};

const pathOf = (url) => {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return url;
  }
};

/* All times on this tab are shown in Lebanon time (the academy's time zone),
   whatever time zone the computer viewing the page is set to. Saved times come
   from the server's US Pacific clock — see serverTime.js. */
const dayKey = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: DISPLAY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const dayLabel = (d) => {
  if (dayKey(d) === dayKey(new Date())) return 'Today';
  if (dayKey(d) === dayKey(new Date(Date.now() - 86400000))) return 'Yesterday';
  const sameYear = dayKey(d).slice(0, 4) === dayKey(new Date()).slice(0, 4);
  return d.toLocaleDateString('en-US', { timeZone: DISPLAY_TZ, weekday: 'long', month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
};

/* Same props as before: { data: { visits, summary }, loading, userType } */
const UserTrackingTab = ({ data, loading, userType }) => {
  if (loading) {
    return (
      <div className="rmaut" aria-busy="true">
        <div className="rmaut-skel rmaut-skel--hero" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rmaut-skel" />
        ))}
      </div>
    );
  }

  const valid = data && typeof data === 'object' && 'visits' in data && 'summary' in data;
  if (!valid) {
    return (
      <div className="rmaut">
        <div className="rmaut-empty">
          <FontAwesomeIcon icon={faChartLine} />
          <p>No activity data available.</p>
        </div>
      </div>
    );
  }

  const visits = Array.isArray(data.visits) ? data.visits : [];
  const summary = data.summary || {};
  const totalVisits = summary.totalVisits || 0;

  // newest first
  const rows = visits
    .map((v, i) => ({ ...v, _i: i, _d: fromServer(v.CreatedAt) }))
    .sort((a, b) => (b._d?.getTime() || 0) - (a._d?.getTime() || 0));

  const last = rows.find((r) => r._d && !Number.isNaN(r._d.getTime()))?._d || null;

  const deviceCounts = { desktop: 0, mobile: 0, tablet: 0, unknown: 0 };
  rows.forEach((r) => {
    deviceCounts[deviceKey(r.DeviceType)] += 1;
  });
  const deviceList = Object.entries(deviceCounts).filter(([, n]) => n > 0);

  const groups = [];
  rows.forEach((r) => {
    const ok = r._d && !Number.isNaN(r._d.getTime());
    const key = ok ? dayKey(r._d) : 'unknown';
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: ok ? dayLabel(r._d) : 'Unknown date', items: [] };
      groups.push(g);
    }
    g.items.push(r);
  });

  return (
    <div className="rmaut">
      {/* ---------- Summary ---------- */}
      <section className="rmaut-hero" aria-label="Activity summary">
        <div className="rmaut-stat">
          <span>
            <FontAwesomeIcon icon={faEye} /> Total visits
          </span>
          <strong>{totalVisits.toLocaleString('en-US')}</strong>
        </div>
        <div className="rmaut-stat">
          <span>
            <FontAwesomeIcon icon={faClock} /> Last active
          </span>
          <strong className="rmaut-stat-sm">{last ? ago(last) : '—'}</strong>
          {last && <small>{last.toLocaleString('en-US', { timeZone: DISPLAY_TZ, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</small>}
        </div>
        <div className="rmaut-stat rmaut-stat--devices">
          <span>Devices · last {rows.length} visits</span>
          {rows.length > 0 ? (
            <>
              <div className="rmaut-bar" aria-hidden="true">
                {deviceList.map(([k, n]) => (
                  <i key={k} className={`is-${k}`} style={{ flexGrow: n }} />
                ))}
              </div>
              <ul className="rmaut-legend">
                {deviceList.map(([k, n]) => (
                  <li key={k} className={`is-${k}`}>
                    <i /> {DEVICES[k].label} <b>{Math.round((n / rows.length) * 100)}%</b>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <strong className="rmaut-stat-sm">—</strong>
          )}
        </div>
      </section>

      {/* ---------- Activity log ---------- */}
      <div className="rmaut-head">
        <h3>Recent activity</h3>
        <span>{WHO[userType] || 'User'} · {rows.length} recent {rows.length === 1 ? 'visit' : 'visits'}</span>
      </div>

      {rows.length === 0 ? (
        <div className="rmaut-empty">
          <FontAwesomeIcon icon={faChartLine} />
          <p>No activity recorded yet.</p>
        </div>
      ) : (
        groups.map((g) => (
          <section key={g.key} className="rmaut-day">
            <h4 className="rmaut-day-title">{g.label}</h4>
            <ul className="rmaut-list">
              {g.items.map((v) => {
                const dk = deviceKey(v.DeviceType);
                const path = pathOf(v.PageUrl);
                const name = pageOf(v);
                return (
                  <li key={v.Id || v._i} className="rmaut-row">
                    <span className="rmaut-time">{v._d && !Number.isNaN(v._d.getTime()) ? v._d.toLocaleTimeString('en-US', { timeZone: DISPLAY_TZ, hour: 'numeric', minute: '2-digit' }) : '—'}</span>
                    <span className="rmaut-page">
                      <strong>{name}</strong>
                      {path && path !== name && <small title={v.PageUrl}>{path}</small>}
                    </span>
                    <span className={`rmaut-device is-${dk}`} title={DEVICES[dk].label}>
                      <FontAwesomeIcon icon={DEVICES[dk].icon} />
                      <span>{v.DeviceType || 'Unknown'}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
};

export default UserTrackingTab;